import {
	Box,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { DelayByShift } from "../models/DelayByShift";

type ShiftData = {
	shift: string;
	durationMin: number;
	eventCount: number;
};

type CrewData = {
	crew: string;
	durationMin: number;
	eventCount: number;
};

export const DelaysCrewShiftBreakdown = ({
	delays,
	sectionName,
}: {
	delays: DelayByShift[];
	sectionName?: string | null;
}) => {
	const shiftMap = new Map<string, { duration: number; count: number }>();
	const crewMap = new Map<string, { duration: number; count: number }>();

	for (const delay of delays) {
		const shift = delay.Shift || "Unknown";
		const crew = delay.Crew || "Unknown";

		const existingShift = shiftMap.get(shift);
		if (existingShift) {
			existingShift.duration += delay.Duration || 0;
			existingShift.count += 1;
		} else {
			shiftMap.set(shift, { duration: delay.Duration || 0, count: 1 });
		}

		const existingCrew = crewMap.get(crew);
		if (existingCrew) {
			existingCrew.duration += delay.Duration || 0;
			existingCrew.count += 1;
		} else {
			crewMap.set(crew, { duration: delay.Duration || 0, count: 1 });
		}
	}

	const shiftData: ShiftData[] = Array.from(shiftMap.entries())
		.map(([shift, data]) => ({
			shift,
			durationMin: data.duration / 60,
			eventCount: data.count,
		}))
		.sort((a, b) => b.durationMin - a.durationMin);

	const crewData: CrewData[] = Array.from(crewMap.entries())
		.map(([crew, data]) => ({
			crew,
			durationMin: data.duration / 60,
			eventCount: data.count,
		}))
		.sort((a, b) => b.durationMin - a.durationMin);

	const renderTable = (
		title: string,
		rows: { label: string; durationMin: number; eventCount: number }[],
	) => (
		<Box sx={{ flex: 1, overflow: "hidden", maxWidth: 500 }}>
			<Box
				sx={{
					px: 2,
					py: 0.75,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Typography
					variant="caption"
					fontWeight={600}
					color="text.secondary"
					sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
				>
					{title}
				</Typography>
			</Box>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>{title === "By Shift" ? "Shift" : "Crew"}</TableCell>
						<TableCell align="right">Duration (min)</TableCell>
						<TableCell align="right">Events</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.label} hover>
							<TableCell>
								<Typography variant="body2" fontWeight={500}>
									{row.label}
								</Typography>
							</TableCell>
							<TableCell align="right">
								<Typography variant="body2">
									{row.durationMin.toFixed(1)}
								</Typography>
							</TableCell>
							<TableCell align="right">
								<Typography variant="body2">{row.eventCount}</Typography>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Box>
	);

	return (
		<Paper variant="outlined" sx={{ mb: 1 }}>
			<Box
				sx={{
					px: 2,
					py: 1,
					backgroundColor: (theme) => theme.palette.primary.main,
				}}
			>
				<Typography variant="subtitle2" sx={{ color: "white" }}>
					{sectionName ? `${sectionName} — ` : ""}Delay Breakdown by Crew &
					Shift
				</Typography>
			</Box>

			<Divider />

			<Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
				{renderTable(
					"By Shift",
					shiftData.map((r) => ({
						label: r.shift,
						durationMin: r.durationMin,
						eventCount: r.eventCount,
					})),
				)}
				{renderTable(
					"By Crew",
					crewData.map((r) => ({
						label: r.crew,
						durationMin: r.durationMin,
						eventCount: r.eventCount,
					})),
				)}
			</Box>
		</Paper>
	);
};
